import { StorageAdapter } from '../../storage/StorageAdapter'
import { MetaRepository } from '../../repositories/MetaRepository'
import { extractPage, upsertCatalog } from './upsert'
import { clearPullFailure, isPullBackedOff, recordPullFailure, PULL_EMPTY_GUARD_REASON } from './pull-backoff'
import { dbg, dbgWarn } from '../../logger'

/**
 * pull-engine.js — el CORE SWR del pull Tier B de offline-v2 (PULL-01).
 *
 * Se apoya en las costuras existentes: SQL via StorageAdapter (STOR-05), red via el
 * descriptor.fetchPage (que 05-02 implementa con getTransport(), PUSH-05), cursor en
 * meta bajo `cursor:<catalog>` (RESERVADO, MetaRepository.js:18).
 *
 * Aporta: registro de descriptores por-catalogo + orquestacion SWR (TTL via cursor,
 * `since`-cursor-ready), un EMPTY-RESPONSE GUARD (B1) para que un 200 vacio transitorio
 * NUNCA pueda vaciar un catalogo poblado, y un getScope() (I6) para que los descriptores
 * resuelvan org/user cuando el caller pasa scope=undefined.
 *
 * NO importa axios ni el plugin ni v1. El upsert/extractPage vienen de ./upsert.
 */

const registry = Object.create(null)
const MAX_PAGES = 1000 // backstop anti-loop si el server pagina mal.

/**
 * SCOPE RESOLVER (I6) — costura inyectable. Mantiene el pull-engine PURO y desacoplado
 * del store Pinia (que arrastra Quasar/Vue y rompe la importabilidad en vitest). Por defecto
 * devuelve undefined; el BOOT (o 05-02) cablea el resolver real via setScopeResolver() con un
 * lector del usuario autenticado, p.ej.:
 *
 *   import { useUsersStore } from 'src/stores'
 *   setScopeResolver(() => {
 *     const u = useUsersStore().current
 *     if (!u) return undefined
 *     const organizationId = (u.organization && u.organization.id) || u.franchise_organization_id || u.organization_id
 *     return organizationId ? { organizationId, userId: u.id } : undefined
 *   })
 *
 * (Misma derivacion que v1 tier-b-bootstrap.tierBScopeFromUser.) Si el resolver lanza o no hay
 * sesion, getScope devuelve undefined (NUNCA lanza) y los descriptores que necesitan scope saltan
 * limpio ({skipped:'no-scope'}).
 */
const defaultScopeResolver = () => undefined
let _scopeResolver = defaultScopeResolver

/** Cablea el resolver de scope real (boot/05-02). Llamar con null restablece el default. */
export function setScopeResolver (fn) {
  _scopeResolver = fn || defaultScopeResolver
}

/**
 * POST-FORMS HOOK (B2) — costura inyectable para prefetchFormChildren (05-02). Se mantiene como
 * setter (en vez de un import estatico de catalogs.js) para EVITAR el ciclo de imports
 * catalogs.js → pull-engine.js → catalogs.js. catalogs.js cablea su prefetchFormChildren via
 * setPostFormsHook(); el default es no-op. bootstrapTierB lo invoca TRAS pulear c_forms (orden LOCKED:
 * c_forms pull → prefetchFormChildren).
 */
let _postFormsHook = null
export function setPostFormsHook (fn) {
  _postFormsHook = fn || null
}

/** Alias de test (espeja la ergonomia _clearRegistry). */
export const _setScopeResolver = setScopeResolver

/**
 * getScope(passedScope) (I6) — resuelve org/user con tolerancia a scope=undefined.
 *  - scope truthy con organizationId => se devuelve tal cual.
 *  - si no, intenta resolver de la sesion/auth store.
 *  - si nada resuelve => undefined (los callers DEBEN tolerarlo). NUNCA lanza,
 *    NUNCA dereferencia scope.organizationId sobre un scope undefined.
 */
export function getScope (passedScope) {
  if (passedScope && passedScope.organizationId) return passedScope
  try {
    return _scopeResolver()
  } catch {
    return undefined
  }
}

/**
 * DESCRIPTOR SHAPE (la abstraccion central que 05-02 consume):
 * {
 *   name,                  // nombre logico, tambien el sufijo de la cursor key.
 *   table,                 // tabla c_* destino.
 *   ttl,                   // ventana de staleness en ms (SWR).
 *   lazy = false,          // true = NO se pulea en bootstrap/revalidate.
 *   enabled = true,        // false = registrado pero saltado (ej. c_tags gated).
 *   keyColumns = ['id'],
 *   columns,               // columnas c_* para el upsert.
 *   needsScope = false,    // true = mapRow/fetchPage leen scope.organizationId.
 *   sinceField,            // (DISENADO-IN) nombre del campo updated_at para el cursor delta.
 *   fetchPage,             // async (scope, page, since?) => body crudo; el engine lo pasa por extractPage.
 *   mapRow                 // (row, scope) => objeto keyed por columns.
 * }
 */
export function registerCatalog (def) {
  registry[def.name] = def
}

/** Test helper (espeja endpoints.js _clearRegistry). */
export function _clearRegistry () {
  for (const k of Object.keys(registry)) delete registry[k]
}

/**
 * pullCatalog — descarga todas las paginas de un catalogo y lo upsertea en UNA tx.
 *
 * BACKOFF (TKT-2240): el resultado alimenta la marca `cursor:fail:<name>` (pull-backoff.js):
 * exito real => se BORRA; excepcion o empty-response-guard => se abre/escala la ventana de
 * espera que revalidateStale respeta. Aqui NO se consulta esa ventana a proposito: pullCatalog
 * es el camino EXPLICITO (bootstrap tras login, prefetch de un form abierto, QA) y debe poder
 * intentarlo siempre; el gate vive en el carril automatico (revalidateStale).
 *
 * @returns {Promise<{total?:number, skipped?:string}>}
 */
export async function pullCatalog (name, scope, { db = StorageAdapter } = {}) {
  const def = registry[name]
  if (!def) throw new Error(`Catalogo Tier B no registrado: ${name}`)

  if (def.enabled === false) return { skipped: 'disabled' }

  const s = getScope(scope)
  if (def.needsScope && (s === undefined || s === null)) {
    dbgWarn('pull', 'pull skipped, no scope', { name })
    // NO se registra fallo: no hay sesion resuelta todavia, no es un fallo del catalogo, y no
    // se gasto ni un request (el skip es puramente local).
    return { skipped: 'no-scope' }
  }

  try {
    return await pullCatalogPages(name, def, s, { db })
  } catch (error) {
    await recordPullFailure(name, { error })
    throw error
  }
}

/** Recorre las paginas del catalogo y devuelve las filas ya mapeadas + el mayor `sinceField`. */
async function fetchAllPages (def, s, sinceCursor) {
  const allMapped = []
  let maxSince
  let page = 1

  while (page <= MAX_PAGES) {
    const body = await def.fetchPage(s, page, sinceCursor)
    const { rows, hasMore } = extractPage(body)
    if (!rows.length) break
    for (const r of rows) {
      allMapped.push(def.mapRow(r, s))
      const v = def.sinceField ? r[def.sinceField] : null
      if (v && (!maxSince || v > maxSince)) maxSince = v
    }
    if (!hasMore) break
    page += 1
  }

  return { allMapped, maxSince }
}

/**
 * EMPTY-RESPONSE GUARD (B1) — corre DESPUES del paginado y ANTES del upsert; solo lecturas, sin tx.
 * Devuelve true cuando hay que abortar el pull porque el catalogo YA tenia datos: un 200 vacio
 * transitorio no debe vaciarlo.
 */
async function emptyResponseGuardTripped (name, def, { db }) {
  const priorCursor = await MetaRepository.get('cursor:' + name)
  const populated = priorCursor != null || (await tableCount(db, def.table)) > 0

  // Nunca-poblado + 0 filas = catalogo legitimamente vacio: no-op legal, el cursor se escribe.
  if (!populated) return false

  dbgWarn('pull', 'empty-response guard: refusing to wipe populated catalog', { name })
  // NO tocar el cursor: un 200 vacio transitorio no debe avanzar la frescura. Pero SIN una
  // ventana de espera eso deja el catalogo stale para siempre y el carril automatico lo
  // re-pullea cada 30 s indefinidamente (TKT-2240) -> entra a la escalera de backoff.
  await recordPullFailure(name, { reason: PULL_EMPTY_GUARD_REASON })
  return true
}

/**
 * Cuerpo del pull (paginado + guard + upsert + cursor). Separado de pullCatalog SOLO para que la
 * contabilidad de fallos envuelva todo el camino con un unico try, sin anidar el flujo feliz.
 */
async function pullCatalogPages (name, def, s, { db }) {
  const keyColumns = def.keyColumns || ['id']
  const sinceCursor = await readSince(name)
  const { allMapped, maxSince } = await fetchAllPages(def, s, sinceCursor)

  if (allMapped.length === 0 && await emptyResponseGuardTripped(name, def, { db })) {
    return { skipped: 'empty-response-guard' }
  }

  // SCOPED FULL-REFRESH (aditivo 05-02): los catalogos LAZY por-clave (form_groups por form_id,
  // form_items por group_id, catalog_elements por catalog_id) NO deben borrar la tabla ENTERA — eso
  // borraria los hijos de otros forms ya cacheados. Si el descriptor define scopedDelete(scope) =>
  // {whereSql, whereParams}, el full-refresh borra SOLO ese subconjunto. Sin scopedDelete, full-replace
  // de toda la tabla como en 05-01 (forms/locations/users/roles/areas/hierarchies).
  const scoped = typeof def.scopedDelete === 'function' ? def.scopedDelete(s) : null
  await upsertCatalog(
    {
      table: def.table,
      keyColumns,
      columns: def.columns,
      rows: allMapped,
      fullRefresh: true,
      whereSql: scoped ? scoped.whereSql : undefined,
      whereParams: scoped ? scoped.whereParams : []
    },
    { db }
  )

  await MetaRepository.put(
    'cursor:' + name,
    JSON.stringify({ last_pull_at: Date.now(), total: allMapped.length, since: def.sinceField ? maxSince : undefined })
  )
  // Exito REAL (cursor escrito): se borra la marca de fallo para que el proximo tropiezo vuelva a
  // empezar la escalera desde 1 min en vez de heredar horas de espera.
  await clearPullFailure(name)

  return { total: allMapped.length }
}

/**
 * True si nunca se pulleo o ya paso su TTL. (Acepta opts por consistencia de firma con el resto
 * del engine; la lectura del cursor va por MetaRepository, no por el db inyectado.)
 */
export async function isStale (name) {
  const def = registry[name]
  const raw = await MetaRepository.get('cursor:' + name)
  if (!raw) return true
  let parsed
  try { parsed = JSON.parse(raw) } catch { return true }
  const ttl = (def && def.ttl) || 3600000
  return (Date.now() - parsed.last_pull_at) > ttl
}

/**
 * bootstrapTierB — pulea todos los descriptores no-lazy y enabled, best-effort.
 * Pensado para llamarse una vez tras login (online).
 */
export async function bootstrapTierB (scope, { db = StorageAdapter } = {}) {
  const s = getScope(scope)
  for (const name of Object.keys(registry)) {
    const def = registry[name]
    if (def.lazy || def.enabled === false) continue
    try {
      await pullCatalog(name, s, { db })
      // ORDEN LOCKED (B2): JUSTO tras pulear c_forms, calienta los groups/items de los forms
      // asignados del operador para que pueda abrirlos offline. Best-effort (no rompe el bootstrap).
      if (name === 'forms' && _postFormsHook) {
        try {
          await _postFormsHook(s, { db })
        } catch (error) {
          dbgWarn('pull', 'prefetchFormChildren hook failed', error)
        }
      }
    } catch (error) {
      dbgWarn('pull', 'bootstrapTierB catalog failed: ' + name, error)
    }
  }
}

/**
 * revalidateStale — pulea solo los catalogos stale, best-effort. Lo llama el tick del
 * sync-worker (que ya gatea isReallyOnline()) — puede pasar undefined y getScope resuelve.
 *
 * BACKOFF POR CATALOGO (TKT-2240): este es el carril DESATENDIDO (cada 30 s, para siempre). Un
 * catalogo que viene fallando se SALTA hasta que venza su ventana (pull-backoff.js). Sin esto, un
 * catalogo que nunca logra escribir su cursor se re-descarga COMPLETO cada tick: con `users`
 * (~6k filas, ~30 requests) eran cientos de MB por hora y por device.
 *
 * `force` = el usuario pidio sincronizar a proposito (boton "Sincronizar ahora", cierre de la
 * auditoria). Entonces el gate NO aplica. Sin esta salida, un catalogo en el escalon de 6 h se
 * quedaba congelado sin forma de recuperarlo: bootstrapTierB —la "llamada explicita" que el diseno
 * del backoff daba por hecha— solo esta cableado bajo process.env.DEV, asi que en produccion el
 * unico camino a los catalogos es este, y esta gateado. Un supervisor con mala cobertura acumulaba
 * fallos hasta el tope y ya no refrescaba usuarios/ubicaciones/formularios en toda la jornada.
 */
export async function revalidateStale (scope, { db = StorageAdapter, force = false } = {}) {
  const s = getScope(scope)
  for (const name of Object.keys(registry)) {
    const def = registry[name]
    if (def.lazy || def.enabled === false) continue
    try {
      if (!force && await isPullBackedOff(name)) {
        dbg('pull', 'revalidate saltado por backoff: ' + name)
        continue
      }
      if (await isStale(name)) await pullCatalog(name, s, { db })
    } catch (error) {
      dbgWarn('pull', 'revalidateStale catalog failed: ' + name, error)
    }
  }
}

// ── helpers internos ────────────────────────────────────────────────────────

async function readSince (name) {
  const raw = await MetaRepository.get('cursor:' + name)
  if (!raw) return undefined
  try { return JSON.parse(raw).since } catch { return undefined }
}

async function tableCount (db, table) {
  const res = await db.query('SELECT COUNT(*) AS n FROM ' + table)
  const row = res && res.values && res.values[0]
  return row ? Number(row.n) : 0
}
